import React, { useEffect, useRef, useState } from 'react';
import { App, Alert, Button, Image, Upload } from 'antd';
import { DeleteOutlined, EyeOutlined, InboxOutlined } from '@ant-design/icons';
import { trpc } from '../../lib/trpc';
import { withDevModalFilePath } from '../common';

const { Dragger } = Upload;
const { Modal } = withDevModalFilePath('client/src/components/location/LocationThumbnailSettings.tsx');

export interface LocationThumbnailSettingsProps {
  locationId: string;
  canEdit: boolean;
}

const LocationThumbnailSettings: React.FC<LocationThumbnailSettingsProps> = ({ locationId, canEdit }) => {
  const { message } = App.useApp();
  const utils = trpc.useUtils();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const localPreviewUrlRef = useRef<string | null>(null);
  const pendingFileIdRef = useRef<string | null>(null);
  const [displayImageUrl, setDisplayImageUrl] = useState<string | null>(null);

  const { data: imageData } = trpc.location.getThumbnailImage.useQuery(
    { id: locationId },
    { enabled: !!locationId }
  );

  useEffect(() => {
    if (imageData?.imageUrl) {
      const matchesPending =
        !pendingFileIdRef.current || imageData.fileId === pendingFileIdRef.current;
      if (matchesPending) {
        setDisplayImageUrl(imageData.imageUrl);
        if (localPreviewUrlRef.current) {
          URL.revokeObjectURL(localPreviewUrlRef.current);
          localPreviewUrlRef.current = null;
        }
        pendingFileIdRef.current = null;
      }
    } else if (!pendingFileIdRef.current && !localPreviewUrlRef.current) {
      setDisplayImageUrl(null);
    }
  }, [imageData?.imageUrl, imageData?.fileId]);

  useEffect(
    () => () => {
      if (localPreviewUrlRef.current) {
        URL.revokeObjectURL(localPreviewUrlRef.current);
      }
    },
    []
  );

  const getUploadUrlMutation = trpc.file.getUploadUrl.useMutation();
  const registerFileMutation = trpc.file.registerFile.useMutation();
  const verifyUploadMutation = trpc.file.verifyUpload.useMutation();

  const setImageMutation = trpc.location.setThumbnailImage.useMutation({
    onSuccess: async () => {
      await utils.location.getThumbnailImage.invalidate({ id: locationId });
    },
    onError: (error) => {
      pendingFileIdRef.current = null;
      message.error(`画像の更新に失敗しました: ${error.message}`);
    },
  });

  const removeImageMutation = trpc.location.removeThumbnailImage.useMutation({
    onSuccess: async () => {
      message.success('サムネイル画像を削除しました');
      await utils.location.getThumbnailImage.invalidate({ id: locationId });
    },
    onError: (error) => {
      message.error(`画像の削除に失敗しました: ${error.message}`);
    },
  });

  const handleUpload = async (file: File) => {
    const previousImageUrl = displayImageUrl ?? null;
    try {
      setUploading(true);

      if (localPreviewUrlRef.current) {
        URL.revokeObjectURL(localPreviewUrlRef.current);
      }
      const previewUrl = URL.createObjectURL(file);
      localPreviewUrlRef.current = previewUrl;
      setDisplayImageUrl(previewUrl);

      const presignedData = await getUploadUrlMutation.mutateAsync({
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        category: 'location-thumbnail',
      });

      const formData = new FormData();
      Object.entries(presignedData.fields).forEach(([key, value]) => {
        formData.append(key, value as string);
      });
      formData.append('file', file);

      try {
        await fetch(presignedData.url, {
          method: 'POST',
          body: formData,
          mode: 'no-cors',
        });
      } catch {
        throw new Error('S3 upload failed. Please check your network connection and try again.');
      }

      try {
        await verifyUploadMutation.mutateAsync({ key: presignedData.key });
      } catch {
        throw new Error('S3 upload verification failed. Please confirm the bucket CORS configuration.');
      }

      const fileRecord = await registerFileMutation.mutateAsync({
        s3Key: presignedData.key,
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type,
        category: 'location-thumbnail',
        associations: [
          {
            entityType: 'location',
            entityId: locationId,
            associationType: 'location-thumbnail',
          },
        ],
      });

      pendingFileIdRef.current = fileRecord.id;

      await setImageMutation.mutateAsync({
        id: locationId,
        fileId: fileRecord.id,
      });

      let serverImageApplied = false;
      try {
        const latestImage = await utils.location.getThumbnailImage.fetch({ id: locationId });
        if (latestImage && latestImage.fileId === fileRecord.id) {
          setDisplayImageUrl(latestImage.imageUrl);
          await utils.location.getThumbnailImage.setData({ id: locationId }, latestImage);
          pendingFileIdRef.current = null;
          serverImageApplied = true;
        }
      } catch {
        // ignore
      } finally {
        if (serverImageApplied && localPreviewUrlRef.current) {
          URL.revokeObjectURL(localPreviewUrlRef.current);
          localPreviewUrlRef.current = null;
        }
      }

      message.success('画像をアップロードしました');
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : '予期しないエラーが発生しました。';
      if (localPreviewUrlRef.current) {
        URL.revokeObjectURL(localPreviewUrlRef.current);
        localPreviewUrlRef.current = null;
      }
      pendingFileIdRef.current = null;
      setDisplayImageUrl(previousImageUrl);
      message.error(`アップロードに失敗しました: ${errorMessage}`);
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    Modal.confirm({
      title: 'サムネイル画像を削除',
      content: '本当にサムネイル画像を削除しますか？',
      okText: '削除',
      okType: 'danger',
      cancelText: 'キャンセル',
      onOk: async () => {
        await removeImageMutation.mutateAsync({ id: locationId });
      },
    });
  };

  return (
    <div>
      <Alert
        message="サムネイル画像について"
        description="マップ上のアイコンとして表示する画像（写真）をアップロードできます。推奨: 正方形（例: 256×256〜512×512）。"
        type="info"
        showIcon
        className="mb-4"
      />

      <div className="mb-6">
        <div className="text-base font-semibold mb-3">現在の画像</div>
        {displayImageUrl ? (
          <div className="relative">
            <div
              style={{
                border: '2px dashed #d9d9d9',
                borderRadius: '8px',
                padding: '16px',
                textAlign: 'center',
                backgroundColor: '#fafafa',
              }}
            >
              <Image
                src={displayImageUrl}
                alt="Location thumbnail"
                style={{ width: 160, height: 160, objectFit: 'cover', borderRadius: 12 }}
                preview={false}
              />
            </div>
            <div className="mt-3 flex gap-2 justify-center">
              <Button
                icon={<EyeOutlined />}
                onClick={() => setPreviewOpen(true)}
                disabled={!displayImageUrl}
              >
                プレビュー
              </Button>
              <Button
                icon={<DeleteOutlined />}
                danger
                onClick={handleRemove}
                disabled={!canEdit || uploading || removeImageMutation.isPending}
              >
                削除
              </Button>
            </div>
          </div>
        ) : (
          <div
            style={{
              border: '2px dashed #d9d9d9',
              borderRadius: '8px',
              padding: '24px',
              textAlign: 'center',
              backgroundColor: '#fafafa',
              color: '#999',
            }}
          >
            まだサムネイル画像がありません
          </div>
        )}
      </div>

      <div>
        <div className="text-base font-semibold mb-3">画像をアップロード</div>
        <Dragger
          name="file"
          multiple={false}
          showUploadList={false}
          accept="image/*"
          disabled={!canEdit || uploading}
          beforeUpload={(file) => {
            void handleUpload(file as File);
            return false;
          }}
          style={{ padding: 16 }}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">クリックまたはドラッグ&ドロップで画像をアップロード</p>
          <p className="ant-upload-hint">PNG / JPG / WebP など</p>
        </Dragger>
      </div>

      <Image
        wrapperStyle={{ display: 'none' }}
        preview={{
          visible: previewOpen,
          src: displayImageUrl ?? undefined,
          onVisibleChange: (visible) => setPreviewOpen(visible),
        }}
      />
    </div>
  );
};

export default LocationThumbnailSettings;

